import { AxiosError, AxiosResponse } from 'axios'
import {
  Page,
  ReservationsGetManyRequest,
  Reservation,
  ReservationCreateRequest,
  ReservationConvertRequest,
  ReservationResponse,
} from '../../../../types'
import { HttpClientConfig } from '../../../useHttpClientConfig'
import { ToastNotifyFunction } from '../../../useNotification'
import { apiVersion, service } from '../service'
import { applySearchParams, responseToPage, urlToPathnameAndSearch } from '../utils'
import { parseISO, secondsToMilliseconds } from 'date-fns'
import { ToastSubType } from '../../../../constants'

const pathname = `/api/${apiVersion}/reservations`
const CONVERT_RESERVATION_MAX_TIMEOUT_IN_SECONDS = 60
const CONVERT_RESERVATION_WARNING_DELAY_IN_SECONDS = 10

const transformReservationResponseAppointment = ({
  created_at,
  expires_at,
  start_date,
  updated_at,
  ...reservation
}: ReservationResponse): Reservation => ({
  ...reservation,
  created_at: parseISO(created_at),
  expires_at: parseISO(expires_at),
  start_date: parseISO(start_date),
  updated_at: parseISO(updated_at),
})

const makeCancelReservation =
  (baseUrl: string, config: HttpClientConfig) =>
  async (id: string): Promise<void> => {
    const pathnameAndParam = `${pathname}/${id}/cancel`
    const url = new URL(pathnameAndParam, baseUrl)
    const pathnameAndSearch = urlToPathnameAndSearch(url)

    await service(baseUrl)
      .patch(pathnameAndSearch, undefined, config)
      .catch((error: AxiosError) => {
        return Promise.reject(error)
      })
    return
  }

const makeConvertReservation =
  (baseUrl: string, config: HttpClientConfig, notify: ToastNotifyFunction) =>
  async (id: string, request: ReservationConvertRequest): Promise<void> => {
    const pathnameAndParam = `${pathname}/${id}/convert`
    const url = new URL(pathnameAndParam, baseUrl)
    const pathnameAndSearch = urlToPathnameAndSearch(url)
    let finished = false

    let warningTimeoutId: NodeJS.Timeout | null = null

    const triggerWarningNotification = () => {
      return new Promise<void>((resolve) => {
        warningTimeoutId = setTimeout(() => {
          if (!finished) {
            notify(
              {
                message: `This is taking longer than expected. Please wait...`,
                type: ToastSubType.CONVERT_RESERVATION_TAKING_LONGER_THAN_EXPECTED,
              },
              'warning'
            )
          }
          resolve()
        }, secondsToMilliseconds(CONVERT_RESERVATION_WARNING_DELAY_IN_SECONDS))
      })
    }

    const convertReservationRequest = async () => {
      await service(baseUrl)
        .patch(pathnameAndSearch, request, { ...config, timeout: secondsToMilliseconds(CONVERT_RESERVATION_MAX_TIMEOUT_IN_SECONDS) })
        .catch((error: AxiosError) => {
          return Promise.reject(error)
        })
        .finally(() => {
          finished = true
        })
    }

    await Promise.race([triggerWarningNotification(), convertReservationRequest()])

    // Clear the warning timeout if it hasn't been triggered yet
    if (warningTimeoutId) {
      clearTimeout(warningTimeoutId)
    }
  }

const makeCreateReservation =
  (baseUrl: string, config: HttpClientConfig) =>
  async (request: ReservationCreateRequest): Promise<Reservation> => {
    const { data } = await service(baseUrl)
      .post<ReservationCreateRequest, AxiosResponse<Reservation>>(pathname, request, config)
      .catch((error: AxiosError) => {
        return Promise.reject(error)
      })
    return data
  }

const makeGetReservation =
  (baseUrl: string, config: HttpClientConfig) =>
  async (id: string): Promise<Reservation | undefined> => {
    const pathnameAndParam = `${pathname}/${id}`
    const url = new URL(pathnameAndParam, baseUrl)

    const pathnameAndSearch = urlToPathnameAndSearch(url)
    const response = await service(baseUrl).get(pathnameAndSearch, config)

    return transformReservationResponseAppointment(response.data)
  }

const makeGetReservations =
  (baseUrl: string, config: HttpClientConfig) =>
  async (filters: ReservationsGetManyRequest): Promise<Page<Reservation>> => {
    const url = new URL(pathname, baseUrl)

    applySearchParams(url, { page_size: 100, ...filters })

    const pathnameAndSearch = urlToPathnameAndSearch(url)
    const response = await service(baseUrl).get(pathnameAndSearch, config)

    return responseToPage<Reservation>(response)
  }

export { makeCancelReservation, makeConvertReservation, makeCreateReservation, makeGetReservation, makeGetReservations }
